import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  AfterViewInit,
  ElementRef,
} from '@angular/core';
import Utils from 'src/app/helpers/utils';
import { PersonIncomeOthersValues } from '../../signup-unified-person/signup-unified-person-income/signup-unified-person-income-others/signup-unified-person-income-others.component';
import { ApiService } from 'src/app/services/api/api.service';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

export type PersonIncomeRealtyValues = {
  type: string;
  current_value: string;
  address: {
    cep: string;
    public_place: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    uf: string;
    country: string;
  };
};

@Component({
  selector: 'app-dashboard-approval-income',
  templateUrl: './dashboard-approval-income.component.html',
  styleUrls: ['./dashboard-approval-income.component.scss'],
})
export class DashboardApprovalIncomeComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() comparingVersions: boolean = false;
  columns: TableColumn<any>[] = [
    { name: 'Tipo do imóvel', cell: (row) => row.type || '--' },
    {
      name: 'Endereço completo',
      cell: (row) => `
          ${
            row.address && row.address.cep
              ? ` <span>
                ${row.address.public_place}, ${row.address.number}, ${
                  row.address.complement ?? ''
                } ${row.address.district}
                </span>
                <span class="table-subtitle">${row.address.city}, ${
                  row.address.uf
                } — ${row.address.cep}</span>`
              : '--'
          }
    
          `,
    },
    {
      name: 'Valor atual',
      cell: (row) => Utils.formatBrl(Number(row.current_value)),
    },
  ];

  othersColumns: any[] = [
    { name: 'Tipo', cell: (row) => row.type },
    {
      name: 'Descrição',
      cell: (row) => row.description,
    },
    {
      name: 'Valor atual',
      cell: (row) => Utils.formatBrl(Number(row.current_value)),
    },
  ];

  getColumnsForView(
    versionType: 'main' | 'comparing' | null,
    isProperty: boolean = true
  ): TableColumn<any>[] {
    if (!this.comparingVersions) {
      return isProperty ? this.columns : this.othersColumns;
    }

    if (isProperty) {
      return [
        {
          name: 'Tipo',
          cell: (row: PersonIncomeRealtyValues) => {
            const icon = this.getItemIcon(row, versionType, true);
            const fieldIcon = this.getFieldComparisonIcon(
              row,
              'type',
              versionType,
              true
            );
            const rowClass = this.getItemRowClass(row, versionType, true);
            const strikethrough =
              this.getItemStatus(row, versionType, true) === 'removed'
                ? 'text-decoration-line-through'
                : '';

            return `
              <div class="row-compare-wrapper ${rowClass}" style="display: flex; align-items: center; gap: 3px; flex-direction: row;">
                <div style="flex-shrink: 0; display: flex; align-items: center; gap: 3px;">
                  ${icon}
                  ${fieldIcon}
                </div>
                <div class="${strikethrough}">${row.type || '--'}</div>
              </div>
            `;
          },
        },
        {
          name: 'Endereço completo',
          cell: (row: PersonIncomeRealtyValues) => {
            const fieldIcon = this.getFieldComparisonIcon(
              row,
              'address',
              versionType,
              true
            );
            const strikethrough =
              this.getItemStatus(row, versionType, true) === 'removed'
                ? 'text-decoration-line-through'
                : '';

            const addressHtml =
              row.address && row.address.cep
                ? `<span class="${strikethrough}">
                  ${row.address.public_place}, ${row.address.number}, ${
                    row.address.complement ?? ''
                  } ${row.address.district}
                </span>
                <span class="table-subtitle ${strikethrough}">${
                    row.address.city
                  }, ${row.address.uf} — ${row.address.cep}</span>`
                : '--';

            return `
              <div style="display: flex; align-items: center; gap: 3px; flex-direction: row;">
                <div style="flex-shrink: 0;">${fieldIcon}</div>
                <div>${addressHtml}</div>
              </div>
            `;
          },
        },
        {
          name: 'Valor atual',
          cell: (row: PersonIncomeRealtyValues) => {
            const fieldIcon = this.getFieldComparisonIcon(
              row,
              'current_value',
              versionType,
              true
            );
            const strikethrough =
              this.getItemStatus(row, versionType, true) === 'removed'
                ? 'text-decoration-line-through'
                : '';

            return `
              <div style="display: flex; align-items: center; gap: 3px; flex-direction: row;">
                <div style="flex-shrink: 0;">${fieldIcon}</div>
                <div class="${strikethrough}">${Utils.formatBrl(
              Number(row.current_value)
            )}</div>
              </div>
            `;
          },
        },
      ];
    } else {
      return [
        {
          name: 'Tipo',
          cell: (row: Partial<PersonIncomeOthersValues>) => {
            const icon = this.getItemIcon(row, versionType, false);
            const fieldIcon = this.getFieldComparisonIcon(
              row,
              'type',
              versionType,
              false
            );
            const rowClass = this.getItemRowClass(row, versionType, false);
            const strikethrough =
              this.getItemStatus(row, versionType, false) === 'removed'
                ? 'text-decoration-line-through'
                : '';

            return `
              <div class="row-compare-wrapper ${rowClass}" style="display: flex; align-items: center; gap: 3px; flex-direction: row;">
                <div style="flex-shrink: 0; display: flex; align-items: center; gap: 3px;">
                  ${icon}
                  ${fieldIcon}
                </div>
                <div class="${strikethrough}">${row.type || '--'}</div>
              </div>
            `;
          },
        },
        {
          name: 'Descrição',
          cell: (row: Partial<PersonIncomeOthersValues>) => {
            const fieldIcon = this.getFieldComparisonIcon(
              row,
              'description',
              versionType,
              false
            );
            const strikethrough =
              this.getItemStatus(row, versionType, false) === 'removed'
                ? 'text-decoration-line-through'
                : '';

            return `
              <div style="display: flex; align-items: center; gap: 3px; flex-direction: row;">
                <div style="flex-shrink: 0;">${fieldIcon}</div>
                <div class="${strikethrough}">${row.description || '--'}</div>
              </div>
            `;
          },
        },
        {
          name: 'Valor atual',
          cell: (row: Partial<PersonIncomeOthersValues>) => {
            const fieldIcon = this.getFieldComparisonIcon(
              row,
              'current_value',
              versionType,
              false
            );
            const strikethrough =
              this.getItemStatus(row, versionType, false) === 'removed'
                ? 'text-decoration-line-through'
                : '';

            return `
              <div style="display: flex; align-items: center; gap: 3px; flex-direction: row;">
                <div style="flex-shrink: 0;">${fieldIcon}</div>
                <div class="${strikethrough}">${Utils.formatBrl(
              Number(row.current_value)
            )}</div>
              </div>
            `;
          },
        },
      ];
    }
  }

  properties: PersonIncomeRealtyValues[] = [];
  mainProperties: PersonIncomeRealtyValues[] = [];
  comparingProperties: PersonIncomeRealtyValues[] = [];

  footerData: Partial<PersonIncomeRealtyValues>[] = [];
  mainFooterData: Partial<PersonIncomeRealtyValues>[] = [];
  comparingFooterData: Partial<PersonIncomeRealtyValues>[] = [];

  others: Partial<PersonIncomeOthersValues>[] = [];
  mainOthers: Partial<PersonIncomeOthersValues>[] = [];
  comparingOthers: Partial<PersonIncomeOthersValues>[] = [];
  isPj: boolean = false;
  mainIsPj: boolean = false;
  comparingIsPj: boolean = false;

  othersFooterData: Partial<PersonIncomeOthersValues>[] = [];
  mainOthersFooterData: Partial<PersonIncomeOthersValues>[] = [];
  comparingOthersFooterData: Partial<PersonIncomeOthersValues>[] = [];
  wage: any;
  mainWage: any;
  comparingWage: any;
  loading = true;
  mainLoading = false;
  comparingLoading = false;

  private eventMainVersion: any;
  private eventComparingVersion: any;
  private eventVersion: any;

  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      // Sempre buscar dados iniciais (v2 ou v3) igual outras abas,
      // usando last=true quando não houver versão selecionada
      this.getApprovalData();
    }

    // Listener para mudanças de versão (aba de comparação de versões)
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();

        // Ativar loading em todas as abas quando trocar de versão
        this.loading = true;
        this.getApprovalData();
      }
    });

    // Subscribir aos eventos de mudança de versão
    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
          // O applyComparisonStyles já é chamado dentro de loadComparingVersion
        }
      });
  }

  ngOnDestroy(): void {
    if (this.eventVersion) {
      this.eventVersion.unsubscribe();
    }
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.comparingVersions) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.applyComparisonStyles();
          });
        });
      }, 0);
    }
  }

  loadComparingVersions() {
    // Limpar dados anteriores
    this.mainProperties = [];
    this.mainOthers = [];
    this.mainWage = null;
    this.mainIsPj = false;
    this.mainFooterData = [];
    this.mainOthersFooterData = [];
    this.mainLoading = false;

    this.comparingProperties = [];
    this.comparingOthers = [];
    this.comparingWage = null;
    this.comparingIsPj = false;
    this.comparingFooterData = [];
    this.comparingOthersFooterData = [];
    this.comparingLoading = false;

    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainProperties = [];
      this.mainOthers = [];
      this.mainWage = null;
      this.mainIsPj = false;
      this.mainFooterData = [];
      this.mainOthersFooterData = [];
      this.mainLoading = false;
      return;
    }

    this.mainLoading = true;
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingProperties = [];
      this.comparingOthers = [];
      this.comparingWage = null;
      this.comparingIsPj = false;
      this.comparingFooterData = [];
      this.comparingOthersFooterData = [];
      this.comparingLoading = false;
      return;
    }

    this.comparingLoading = true;
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      const registerType =
        this.approvalService.approvalData?.approval?.register?.type;

      // Para PF (ex.: cotista PF), usar API v3 (wages, properties, other_assets)
      if (registerType === 'PF') {
        await this.getApprovalDataV3(versionType);
      } else {
        // Demais casos (ex.: PJ) continuam usando API v2 atual
        await this.getApprovalDataV2(versionType);
      }
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição normal (não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }
  }

  /**
   * Busca dados na API v2 (comportamento antigo) – usado como fallback (ex.: PJ)
   */
  private async getApprovalDataV2(
    versionType: 'main' | 'comparing' | null = null
  ) {
    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        this.setLoadingState(versionType, false);
        return;
      }

      const req_params: any = {
        register: register_uuid,
        tab: 'assets',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        req_params['version'] = this.approvalService.approvalData.version;
      }

      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      console.log('API v2 Response:', JSON.stringify(data[0]?.register?.wages, null, 2));

      if (versionType === 'main') {
        this.mainProperties = data[0].register.properties || [];
        this.mainOthers = data[0].register.other_assets || [];
        this.mainIsPj = data[0].register.type === 'PJ';
        this.mainWage = data[0].register.wages?.versions?.[0]?.data?.[0] || null;
        this.mainLoading = false;
        this.setFooterData('main');
        this.cdr.detectChanges();
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.applyComparisonStyles();
            });
          });
        }, 0);
      } else if (versionType === 'comparing') {
        this.comparingProperties = data[0].register.properties || [];
        this.comparingOthers = data[0].register.other_assets || [];
        this.comparingIsPj = data[0].register.type === 'PJ';
        this.comparingWage = data[0].register.wages?.versions?.[0]?.data?.[0] || null;
        this.comparingLoading = false;
        this.setFooterData('comparing');
        this.cdr.detectChanges();
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.applyComparisonStyles();
            });
          });
        }, 0);
      } else {
        this.approvalService.approvalData.income = data[0].register;
        this.properties = data[0].register.properties || [];
        this.others = data[0].register.other_assets || [];
        this.isPj = data[0].register.type === 'PJ';
        this.wage = data[0].register.wages?.versions?.[0]?.data?.[0] || null;
        this.loading = false;
        this.setFooterData();
      }
    } catch (error) {
      console.log(error);
      if (versionType === 'main') {
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
      } else {
        this.loading = false;
      }
    }
  }

  setLoadingState(versionType: 'main' | 'comparing' | null, loading: boolean) {
    if (versionType === 'main') {
      this.mainLoading = loading;
    } else if (versionType === 'comparing') {
      this.comparingLoading = loading;
    } else {
      this.loading = loading;
    }
  }

  /**
   * Busca dados na API v3 (wages, properties, other_assets) para PF
   */
  private async getApprovalDataV3(
    versionType: 'main' | 'comparing' | null = null
  ) {
    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        if (versionType === 'main') {
          this.mainProperties = [];
          this.mainOthers = [];
          this.mainWage = null;
          this.mainIsPj = false;
          this.mainFooterData = [];
          this.mainOthersFooterData = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingProperties = [];
          this.comparingOthers = [];
          this.comparingWage = null;
          this.comparingIsPj = false;
          this.comparingFooterData = [];
          this.comparingOthersFooterData = [];
          this.comparingLoading = false;
        } else {
          this.properties = [];
          this.others = [];
          this.wage = null;
          this.isPj = false;
          this.footerData = [];
          this.othersFooterData = [];
          this.loading = false;
        }
        this.cdr.detectChanges();
        return;
      }

      const paramsWage: any = {
        data_type: 'wages',
      };
      const paramsProperties: any = {
        data_type: 'properties',
      };
      const paramsOthers: any = {
        data_type: 'other_assets',
      };

      // Selecionar versão (main/comparing/normal) ou last=true
      let versionToUse: number | null = null;
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }

      if (versionToUse) {
        paramsWage.version = versionToUse;
        paramsProperties.version = versionToUse;
        paramsOthers.version = versionToUse;
      } else {
        paramsWage.last = true;
        paramsProperties.last = true;
        paramsOthers.last = true;
      }

      const [wageResponse, propertiesResponse, othersResponse] =
        await Promise.all([
          this.api.get<any>({
            route: `api/v3/registration/versions/register/${register_uuid}/`,
            params: paramsWage,
            token: true,
          }),
          this.api.get<any>({
            route: `api/v3/registration/versions/register/${register_uuid}/`,
            params: paramsProperties,
            token: true,
          }),
          this.api.get<any>({
            route: `api/v3/registration/versions/register/${register_uuid}/`,
            params: paramsOthers,
            token: true,
          }),
        ]);

      const processed = this.processIncomeV3Data(
        wageResponse,
        propertiesResponse,
        othersResponse
      );

      if (versionType === 'main') {
        this.mainProperties = processed.properties;
        this.mainOthers = processed.otherAssets;
        this.mainWage = processed.wage;
        this.mainIsPj = false;
        this.mainLoading = false;
        this.setFooterData('main');
        this.cdr.detectChanges();
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.applyComparisonStyles();
            });
          });
        }, 0);
      } else if (versionType === 'comparing') {
        this.comparingProperties = processed.properties;
        this.comparingOthers = processed.otherAssets;
        this.comparingWage = processed.wage;
        this.comparingIsPj = false;
        this.comparingLoading = false;
        this.setFooterData('comparing');
        this.cdr.detectChanges();
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.applyComparisonStyles();
            });
          });
        }, 0);
      } else {
        this.properties = processed.properties;
        this.others = processed.otherAssets;
        this.wage = processed.wage;
        this.isPj = false;
        this.loading = false;
        this.setFooterData();

        // Cache no approvalService em formato compatível com o antigo
        this.approvalService.approvalData.income = {
          properties: processed.properties,
          other_assets: processed.otherAssets,
          type: 'PF',
          wages: processed.wage ? [processed.wage] : [],
        } as any;
      }
    } catch (error) {
      console.error('Erro ao carregar renda/patrimônio (v3):', error);
      if (versionType === 'main') {
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
      } else {
        this.loading = false;
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Converte respostas da API v3 (wages, properties, other_assets)
   * no formato esperado pelo componente.
   */
  private processIncomeV3Data(
    wageResponse: any,
    propertiesResponse: any,
    othersResponse: any
  ): {
    wage: any;
    properties: PersonIncomeRealtyValues[];
    otherAssets: Partial<PersonIncomeOthersValues>[];
  } {
    const wageArray =
      wageResponse?.data?.versions?.wages?.versions?.[0]?.data || [];
    const wage =
      Array.isArray(wageArray) && wageArray.length > 0 ? wageArray[0] : null;

    const propertiesArray =
      propertiesResponse?.data?.versions?.properties?.versions?.[0]?.data || [];

    const properties: PersonIncomeRealtyValues[] = Array.isArray(
      propertiesArray
    )
      ? propertiesArray.map((item: any) => {
          const addr = item.address || {};
          return {
            type: item.type || '',
            current_value: item.current_value || '0',
            address: {
              cep: addr.cep || '',
              public_place: addr.public_place || '',
              number: addr.number || '',
              complement: addr.complement || '',
              district: addr.district || '',
              city: addr.city || '',
              uf: addr.uf || '',
              country: addr.country || '',
            },
          } as PersonIncomeRealtyValues;
        })
      : [];

    const othersArray =
      othersResponse?.data?.versions?.other_assets?.versions?.[0]?.data || [];

    const otherAssets: Partial<PersonIncomeOthersValues>[] = Array.isArray(
      othersArray
    )
      ? othersArray.map((item: any) => ({
          type: item.type || '',
          description: item.description || '',
          current_value: item.current_value || '0',
        }))
      : [];

    return {
      wage,
      properties,
      otherAssets,
    };
  }

  setFooterData(versionType: 'main' | 'comparing' | null = null) {
    if (versionType === 'main') {
      const total = String(
        this.mainProperties.reduce(
          (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
          0
        )
      );

      const othersTotal = String(
        this.mainOthers.reduce(
          (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
          0
        )
      );

      this.mainFooterData = [
        {
          type: '<span><strong>Total declarado<strong></span>',
          current_value: total,
        },
      ];

      this.mainOthersFooterData = [
        {
          type: '<span><strong>Total declarado</strong></span>',
          current_value: othersTotal,
        },
      ];
    } else if (versionType === 'comparing') {
      const total = String(
        this.comparingProperties.reduce(
          (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
          0
        )
      );

      const othersTotal = String(
        this.comparingOthers.reduce(
          (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
          0
        )
      );

      this.comparingFooterData = [
        {
          type: '<span><strong>Total declarado<strong></span>',
          current_value: total,
        },
      ];

      this.comparingOthersFooterData = [
        {
          type: '<span><strong>Total declarado</strong></span>',
          current_value: othersTotal,
        },
      ];
    } else {
      const total = String(
        this.properties.reduce(
          (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
          0
        )
      );

      const othersTotal = String(
        this.others.reduce(
          (prev, curr) => Number(prev) + Number(curr.current_value ?? 0),
          0
        )
      );

      this.footerData = [
        {
          type: '<span><strong>Total declarado<strong></span>',
          current_value: total,
        },
      ];

      this.othersFooterData = [
        {
          type: '<span><strong>Total declarado</strong></span>',
          current_value: othersTotal,
        },
      ];
    }
  }

  formatBrl(amount: number | string) {
    const formatted = Utils.formatBrl(Number(amount));

    return formatted;
  }

  // Métodos auxiliares para obter dados baseados no tipo de versão
  getPropertiesForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) return this.properties;
    if (versionType === 'main') return this.mainProperties;
    if (versionType === 'comparing') return this.comparingProperties;
    return this.properties;
  }

  getOthersForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) return this.others;
    if (versionType === 'main') return this.mainOthers;
    if (versionType === 'comparing') return this.comparingOthers;
    return this.others;
  }

  getWageForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) return this.wage;
    if (versionType === 'main') return this.mainWage;
    if (versionType === 'comparing') return this.comparingWage;
    return this.wage;
  }

  getIsPjForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) return this.isPj;
    if (versionType === 'main') return this.mainIsPj;
    if (versionType === 'comparing') return this.comparingIsPj;
    return this.isPj;
  }

  getFooterDataForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) return this.footerData;
    if (versionType === 'main') return this.mainFooterData;
    if (versionType === 'comparing') return this.comparingFooterData;
    return this.footerData;
  }

  getOthersFooterDataForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) return this.othersFooterData;
    if (versionType === 'main') return this.mainOthersFooterData;
    if (versionType === 'comparing') return this.comparingOthersFooterData;
    return this.othersFooterData;
  }

  getLoadingForView(versionType: 'main' | 'comparing' | null = null): boolean {
    if (!this.comparingVersions) return this.loading;
    if (versionType === 'main') return this.mainLoading;
    if (versionType === 'comparing') return this.comparingLoading;
    return this.loading;
  }

  // Métodos de comparação para tabelas
  getItemStatus(
    item: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    versionType: 'main' | 'comparing' | null,
    isProperty: boolean = true
  ): 'modified' | 'added' | 'removed' | 'unchanged' {
    if (!this.comparingVersions || !versionType) return 'unchanged';

    const mainList = isProperty
      ? (this.mainProperties as any[])
      : (this.mainOthers as any[]);
    const comparingList = isProperty
      ? (this.comparingProperties as any[])
      : (this.comparingOthers as any[]);

    // Identificar item por tipo e endereço (para properties) ou tipo e descrição (para others)
    const mainItem = mainList.find((i: any) => {
      if (isProperty) {
        return (
          i.type === item.type &&
          (i as PersonIncomeRealtyValues).address?.cep ===
            (item as PersonIncomeRealtyValues).address?.cep
        );
      } else {
        return (
          i.type === item.type &&
          (i as Partial<PersonIncomeOthersValues>).description ===
            (item as Partial<PersonIncomeOthersValues>).description
        );
      }
    });

    const comparingItem = comparingList.find((i: any) => {
      if (isProperty) {
        return (
          i.type === item.type &&
          (i as PersonIncomeRealtyValues).address?.cep ===
            (item as PersonIncomeRealtyValues).address?.cep
        );
      } else {
        return (
          i.type === item.type &&
          (i as Partial<PersonIncomeOthersValues>).description ===
            (item as Partial<PersonIncomeOthersValues>).description
        );
      }
    });

    if (!mainItem && comparingItem) {
      return versionType === 'comparing' ? 'added' : 'unchanged';
    }

    if (mainItem && !comparingItem) {
      return versionType === 'comparing' ? 'removed' : 'unchanged';
    }

    if (mainItem && comparingItem) {
      const isModified = this.isItemModified(
        mainItem,
        comparingItem,
        isProperty
      );
      return isModified ? 'modified' : 'unchanged';
    }

    return 'unchanged';
  }

  isItemModified(
    mainItem: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    comparingItem: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    isProperty: boolean = true
  ): boolean {
    if (mainItem.type !== comparingItem.type) return true;
    if (mainItem.current_value !== comparingItem.current_value) return true;

    if (isProperty) {
      const mainProp = mainItem as PersonIncomeRealtyValues;
      const comparingProp = comparingItem as PersonIncomeRealtyValues;
      if (
        mainProp.address?.cep !== comparingProp.address?.cep ||
        mainProp.address?.public_place !==
          comparingProp.address?.public_place ||
        mainProp.address?.number !== comparingProp.address?.number ||
        mainProp.address?.complement !== comparingProp.address?.complement ||
        mainProp.address?.district !== comparingProp.address?.district ||
        mainProp.address?.city !== comparingProp.address?.city ||
        mainProp.address?.uf !== comparingProp.address?.uf
      ) {
        return true;
      }
    } else {
      const mainOther = mainItem as Partial<PersonIncomeOthersValues>;
      const comparingOther = comparingItem as Partial<PersonIncomeOthersValues>;
      if (mainOther.description !== comparingOther.description) return true;
    }

    return false;
  }

  getItemRowClass(
    item: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    versionType: 'main' | 'comparing' | null,
    isProperty: boolean = true
  ): string {
    const status = this.getItemStatus(item, versionType, isProperty);
    if (status === 'modified') return 'row-compare-modified';
    if (status === 'added') return 'row-compare-added';
    if (status === 'removed') return 'row-compare-removed';
    return '';
  }

  getItemIcon(
    item: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    versionType: 'main' | 'comparing' | null,
    isProperty: boolean = true
  ): string {
    const status = this.getItemStatus(item, versionType, isProperty);
    if (status === 'added') {
      return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="check" />';
    }
    if (status === 'removed') {
      return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="error" />';
    }
    return '';
  }

  isColumnFieldModified(
    item: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    field: string,
    versionType: 'main' | 'comparing' | null,
    isProperty: boolean = true
  ): boolean {
    if (!this.comparingVersions || versionType !== 'comparing') return false;

    const mainList = isProperty
      ? (this.mainProperties as any[])
      : (this.mainOthers as any[]);
    const comparingList = isProperty
      ? (this.comparingProperties as any[])
      : (this.comparingOthers as any[]);

    const mainItem = mainList.find((i: any) => {
      if (isProperty) {
        return (
          i.type === item.type &&
          (i as PersonIncomeRealtyValues).address?.cep ===
            (item as PersonIncomeRealtyValues).address?.cep
        );
      } else {
        return (
          i.type === item.type &&
          (i as Partial<PersonIncomeOthersValues>).description ===
            (item as Partial<PersonIncomeOthersValues>).description
        );
      }
    });

    const comparingItem = comparingList.find((i: any) => {
      if (isProperty) {
        return (
          i.type === item.type &&
          (i as PersonIncomeRealtyValues).address?.cep ===
            (item as PersonIncomeRealtyValues).address?.cep
        );
      } else {
        return (
          i.type === item.type &&
          (i as Partial<PersonIncomeOthersValues>).description ===
            (item as Partial<PersonIncomeOthersValues>).description
        );
      }
    });

    if (!mainItem || !comparingItem) return false;

    if (field === 'type') {
      return mainItem.type !== comparingItem.type;
    }
    if (field === 'current_value') {
      return mainItem.current_value !== comparingItem.current_value;
    }
    if (field === 'address' && isProperty) {
      const mainProp = mainItem as PersonIncomeRealtyValues;
      const comparingProp = comparingItem as PersonIncomeRealtyValues;
      return (
        mainProp.address?.cep !== comparingProp.address?.cep ||
        mainProp.address?.public_place !==
          comparingProp.address?.public_place ||
        mainProp.address?.number !== comparingProp.address?.number ||
        mainProp.address?.complement !== comparingProp.address?.complement ||
        mainProp.address?.district !== comparingProp.address?.district ||
        mainProp.address?.city !== comparingProp.address?.city ||
        mainProp.address?.uf !== comparingProp.address?.uf
      );
    }
    if (field === 'description' && !isProperty) {
      const mainOther = mainItem as Partial<PersonIncomeOthersValues>;
      const comparingOther = comparingItem as Partial<PersonIncomeOthersValues>;
      return mainOther.description !== comparingOther.description;
    }

    return false;
  }

  getFieldComparisonIcon(
    item: PersonIncomeRealtyValues | Partial<PersonIncomeOthersValues>,
    field: string,
    versionType: 'main' | 'comparing' | null,
    isProperty: boolean = true
  ): string {
    if (
      this.isColumnFieldModified(item, field, versionType, isProperty) &&
      versionType === 'comparing'
    ) {
      return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="warning" />';
    }
    return '';
  }

  // Métodos de comparação para campos do card de salário
  getFieldStatus(
    path: string,
    versionType: 'main' | 'comparing' | null
  ): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions || !versionType) return 'unchanged';

    const mainValue = this.getMainValueForComparison(path);
    const comparingValue = this.getComparingValueForComparison(path);

    if (!mainValue && comparingValue) {
      return versionType === 'comparing' ? 'added' : 'unchanged';
    }

    if (mainValue && !comparingValue) {
      return versionType === 'comparing' ? 'removed' : 'unchanged';
    }

    if (mainValue && comparingValue) {
      const mainNorm = this.normalizeValue(mainValue);
      const comparingNorm = this.normalizeValue(comparingValue);
      if (mainNorm !== comparingNorm) {
        return 'modified';
      }
    }

    return 'unchanged';
  }

  getMainValueForComparison(path: string): any {
    if (!this.comparingVersions || !this.mainWage) return null;

    const keys = path.split('.');
    let value: any = this.mainWage;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  getComparingValueForComparison(path: string): any {
    if (!this.comparingVersions || !this.comparingWage) return null;

    const keys = path.split('.');
    let value: any = this.comparingWage;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  normalizeValue(val: any): string {
    if (val === null || val === undefined || val === '') return '';
    if (typeof val === 'boolean') return val ? 'sim' : 'não';
    if (typeof val === 'string') {
      const trimmed = val.toLowerCase().trim();
      if (trimmed === 'sim' || trimmed === 'yes' || trimmed === 'true')
        return 'sim';
      if (
        trimmed === 'não' ||
        trimmed === 'nao' ||
        trimmed === 'no' ||
        trimmed === 'false'
      )
        return 'não';
      return trimmed;
    }
    return String(val).toLowerCase().trim();
  }

  getWageFieldComparisonIcon(
    path: string,
    versionType: 'main' | 'comparing' | null
  ): string {
    const status = this.getFieldStatus(path, versionType);
    if (status === 'added' && versionType === 'comparing') {
      return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="check" />';
    }
    if (status === 'removed' && versionType === 'comparing') {
      return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="error" />';
    }
    if (status === 'modified' && versionType === 'comparing') {
      return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="warning" />';
    }
    return '';
  }

  getFieldValueForDisplay(
    path: string,
    currentValue: any,
    versionType: 'main' | 'comparing' | null
  ): any {
    const status = this.getFieldStatus(path, versionType);
    if (status === 'removed' && versionType === 'comparing') {
      return this.getMainValueForComparison(path);
    }
    return currentValue;
  }

  getFieldStrikethroughClass(
    path: string,
    versionType: 'main' | 'comparing' | null
  ): string {
    const status = this.getFieldStatus(path, versionType);
    if (status === 'removed' && versionType === 'comparing') {
      return 'text-decoration-line-through';
    }
    return '';
  }

  // Método para aplicar estilos de comparação nas tabelas
  applyComparisonStyles() {
    if (!this.comparingVersions) return;

    // Aplicar estilos apenas no lado direito (comparing)
    const comparingColumn =
      this.el.nativeElement.querySelector('.col-6:last-child');
    if (!comparingColumn) return;

    const comparingTables = comparingColumn.querySelectorAll(
      '.app-table-details-body'
    );

    comparingTables.forEach((tableRow: HTMLElement) => {
      // tableRow é o <ul class="app-table-details-body"> que representa uma linha completa
      // Remover classes anteriores
      tableRow.classList.remove(
        'row-compare-modified',
        'row-compare-added',
        'row-compare-removed'
      );

      // Verificar se há wrapper com classe de comparação na primeira célula (li)
      const firstCell = tableRow.querySelector(
        '.app-table-details-body-row:first-child'
      );
      if (firstCell) {
        const wrapper = firstCell.querySelector('.row-compare-wrapper');
        if (wrapper) {
          if (wrapper.classList.contains('row-compare-modified')) {
            tableRow.classList.add('row-compare-modified');
          } else if (wrapper.classList.contains('row-compare-added')) {
            tableRow.classList.add('row-compare-added');
          } else if (wrapper.classList.contains('row-compare-removed')) {
            tableRow.classList.add('row-compare-removed');
          }
        } else {
          // Se não encontrou wrapper, verificar diretamente no innerHTML
          const innerHTML = firstCell.innerHTML || '';
          if (innerHTML.includes('row-compare-modified')) {
            tableRow.classList.add('row-compare-modified');
          } else if (innerHTML.includes('row-compare-added')) {
            tableRow.classList.add('row-compare-added');
          } else if (innerHTML.includes('row-compare-removed')) {
            tableRow.classList.add('row-compare-removed');
          }
        }
      }
    });
  }

  // Método para obter dados de comparação incluindo itens removidos
  getPropertiesForComparison(
    versionType: 'main' | 'comparing' | null
  ): PersonIncomeRealtyValues[] {
    if (!this.comparingVersions) return this.properties;
    if (versionType === 'main') return this.mainProperties;
    if (versionType === 'comparing') {
      const result: PersonIncomeRealtyValues[] = [...this.comparingProperties];

      // Adicionar itens removidos (existem no main mas não no comparing)
      this.mainProperties.forEach((mainItem) => {
        const exists = this.comparingProperties.find(
          (item) =>
            item.type === mainItem.type &&
            item.address?.cep === mainItem.address?.cep
        );
        if (!exists) {
          result.push(mainItem);
        }
      });

      return result;
    }
    return this.properties;
  }

  getOthersForComparison(
    versionType: 'main' | 'comparing' | null
  ): Partial<PersonIncomeOthersValues>[] {
    if (!this.comparingVersions) return this.others;
    if (versionType === 'main') return this.mainOthers;
    if (versionType === 'comparing') {
      const result: Partial<PersonIncomeOthersValues>[] = [
        ...this.comparingOthers,
      ];

      // Adicionar itens removidos (existem no main mas não no comparing)
      this.mainOthers.forEach((mainItem) => {
        const exists = this.comparingOthers.find(
          (item) =>
            item.type === mainItem.type &&
            item.description === mainItem.description
        );
        if (!exists) {
          result.push(mainItem);
        }
      });

      return result;
    }
    return this.others;
  }
}
